import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { intlShape } from 'react-intl';

import { GridContainer, gridTypes } from '@xingternal/xing-grid';
import testable from '@xingternal/malt-util/testable';
import { NSP_CORE } from '@xingternal/malt-constants';
import { Flavour } from '@xingternal/flavour';
import { matchAny } from '@xingternal/feature-switch';
import { LOGIN_STATE } from '@xingternal/xing-frame-provider';

import Navigator, { NAVIGATOR_TYPES } from '../Navigation';
import Header from '../Header';
import Footer, { LoggedOutFooter } from '../Footer';
import Sidebar from '../Sidebar';
import styles from './XingView.css';

const TestableDiv = testable(NSP_CORE, 'div');

export const XDL_NAVIGATOR_TYPES = {
  XDL_NAVIGATOR_TOP: 'xdl_navigator_top',
  XDL_NAVIGATOR_BOTTOM_STICKY: 'xdl_navigator_bottom_sticky',
  XDL_NAVIGATOR_BOTTOM: 'xdl_navigator_bottom',
};

const XingView = ({
  activeItem,
  children,
  className,
  contentClassName,
  featureSwitches,
  gridClassName,
  hasCTA,
  hasOpenedSidebar,
  hasSidebar,
  helpLink,
  hideButtons,
  homeLink,
  intl: { locale },
  loginLink,
  loginState,
  notifications,
  notrack,
  offsetTop,
  sidebar,
  signupLink,
  teaserSpace,
  testId,
  type,
  whiteHeader,
}) => {
  const styleTag =
    hasOpenedSidebar && offsetTop
      ? { top: -offsetTop + 'px', position: 'relative' }
      : {};

  const displayedTeaserSpace = teaserSpace || (
    <div className={styles.emptyTeaserSpace} />
  );

  const matchSwitches = matchAny(featureSwitches);
  const isHorizontalMobile = matchSwitches(Object.values(XDL_NAVIGATOR_TYPES));
  const isHorizontalMobileTop = matchSwitches([
    XDL_NAVIGATOR_TYPES.XDL_NAVIGATOR_TOP,
  ]);
  return (
    <TestableDiv
      className={classNames(styles.wrapper, className, {
        [styles.openedSidebar]: hasOpenedSidebar,
      })}
      testId={testId}
    >
      <a href="#content" className={styles.skipLink}>
        Skip navigation
      </a>
      <Flavour flavour={isHorizontalMobile ? 'xdl' : 'odl'}>
        <Header
          {...{
            activeItem,
            hasCTA,
            hideButtons,
            helpLink,
            homeLink,
            loginLink,
            loginState,
            notifications,
            signupLink,
            type,
            whiteBG: whiteHeader,
          }}
        />
      </Flavour>

      {isHorizontalMobileTop && (
        <div className={styles.horizontalNavigation}>
          <Flavour flavour="xdl">
            <Navigator
              type={NAVIGATOR_TYPES.HORIZONTAL}
              activeItem={activeItem}
            />
          </Flavour>
        </div>
      )}

      {/* If LoggedIn or SoftLoggedIn */}
      {loginState !== LOGIN_STATE.loggedOut && (
        <div style={styleTag}>{displayedTeaserSpace}</div>
      )}

      <div
        className={classNames(styles.shell, {
          [styles[`${type}Shell`]]: Boolean(type),
          [styles.contentOpenSidebar]: hasOpenedSidebar,
        })}
        style={styleTag}
      >
        {/* If LoggedIn or SoftLoggedIn */}
        {loginState !== LOGIN_STATE.loggedOut && hasSidebar && (
          <Sidebar
            className={classNames({
              [styles.sidebar]: isHorizontalMobile,
            })}
            notrack={notrack}
          >
            {sidebar || (
              <Navigator
                type={NAVIGATOR_TYPES.VERTICAL}
                activeItem={activeItem}
              />
            )}
          </Sidebar>
        )}
        <section className={styles.scrollWrapper}>
          <div className={classNames(styles.container, contentClassName)}>
            <main id="content" className={styles.main}>
              <GridContainer className={gridClassName} type={type}>
                {children}
              </GridContainer>
            </main>
          </div>
        </section>
      </div>

      {loginState === LOGIN_STATE.loggedOut ? (
        <LoggedOutFooter locale={locale} />
      ) : (
        <Footer className={styles.footer} collapsible={false} locale={locale} />
      )}
    </TestableDiv>
  );
};

XingView.defaultProps = {
  type: 'standard',
  hasSidebar: true,
};

XingView.propTypes = {
  activeItem: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  featureSwitches: PropTypes.array.isRequired,
  gridClassName: PropTypes.string,
  hasCTA: PropTypes.bool,
  hasOpenedSidebar: PropTypes.bool,
  hasSidebar: PropTypes.bool,
  helpLink: PropTypes.string,
  hideButtons: PropTypes.bool,
  homeLink: PropTypes.string,
  intl: intlShape.isRequired,
  loginLink: PropTypes.string,
  loginState: PropTypes.oneOf(Object.values(LOGIN_STATE)).isRequired,
  notifications: PropTypes.object,
  notrack: PropTypes.bool,
  offsetTop: PropTypes.number,
  sidebar: PropTypes.node,
  signupLink: PropTypes.string,
  teaserSpace: PropTypes.node,
  testId: PropTypes.string,
  type: gridTypes,
  whiteHeader: PropTypes.bool,
};

export default XingView;
